import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Testimonial {
  name: string;
  role: string;
  company: string;
  image: string;
  alt: string;
  content: string;
  metrics: {
    label: string;
    value: string;
  }[];
}

const TestimonialsSection = () => {
  const testimonials: Testimonial[] = [
  {
    name: 'Sarah Mitchell',
    role: 'VP of Customer Experience',
    company: 'TechFlow Solutions',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ffde516e-1763293580273.png",
    alt: 'Professional woman with shoulder-length brown hair in navy blazer smiling at camera in modern office',
    content: 'RedLetterX AI transformed our support operations completely. We went from 12-hour response times to under 3 minutes, and our customer satisfaction scores jumped from 72% to 96%. The ROI was evident within the first month.',
    metrics: [
    { label: 'Response Time', value: '12hrs → 3min' },
    { label: 'Satisfaction', value: '72% → 96%' },
    { label: 'Cost Savings', value: '$180K/year' }]

  },
  {
    name: 'Michael Chen',
    role: 'Director of Operations',
    company: 'GlobalRetail Inc',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_162e380bf-1763296883657.png",
    alt: 'Asian man in glasses wearing white dress shirt smiling confidently in corporate setting',
    content: 'The AI handles 75% of our support tickets automatically, freeing our team to focus on complex customer relationships. Implementation was seamless, and the system learned our processes incredibly fast. Best technology investment we\'ve made.',
    metrics: [
    { label: 'Automation Rate', value: '75%' },
    { label: 'Team Efficiency', value: '+240%' },
    { label: 'Ticket Volume', value: '+150% handled' }]

  },
  {
    name: 'Jennifer Rodriguez',
    role: 'Chief Customer Officer',
    company: 'FinanceHub',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c0f48e8d-1763295027844.png",
    alt: 'Hispanic woman with long dark hair in professional attire smiling warmly in bright office environment',
    content: 'What impressed us most was how the AI maintained our brand voice while delivering faster, more consistent support. Our NPS score increased by 34 points, and we reduced our support team costs by 58% while improving service quality.',
    metrics: [
    { label: 'NPS Increase', value: '+34 points' },
    { label: 'Cost Reduction', value: '58%' },
    { label: 'Quality Score', value: '98/100' }]

  }];


  return (
    <section className="py-20 lg:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full mb-6">
            <Icon name="StarIcon" size={20} variant="solid" />
            <span className="text-sm font-heading font-semibold">Client Success Stories</span>
          </div>
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-6">
            Real Results from Real Businesses
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            See how leading companies transformed their customer support with our AI solutions
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) =>
          <div
            key={index}
            className="bg-card rounded-2xl p-8 border border-border hover:border-accent/50 hover:shadow-elevated transition-all duration-300">

              {/* Rating */}
              <div className="flex items-center space-x-1 mb-6">
                {[...Array(5)].map((_, i) =>
              <Icon key={i} name="StarIcon" size={20} className="text-accent" variant="solid" />
              )}
              </div>

              {/* Content */}
              <p className="text-foreground leading-relaxed mb-8 italic">
                "{testimonial.content}"
              </p>

              {/* Metrics */}
              <div className="space-y-3 mb-8 pb-8 border-b border-border">
                {testimonial.metrics.map((metric, metricIndex) =>
              <div key={metricIndex} className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{metric.label}</span>
                    <span className="text-sm font-heading font-bold text-accent">{metric.value}</span>
                  </div>
              )}
              </div>

              {/* Author */}
              <div className="flex items-center space-x-4">
                <div className="relative w-14 h-14 rounded-full overflow-hidden flex-shrink-0">
                  <AppImage
                  src={testimonial.image}
                  alt={testimonial.alt}
                  className="w-full h-full object-cover" />

                </div>
                <div>
                  <div className="font-heading font-bold text-foreground">{testimonial.name}</div>
                  <div className="text-sm text-muted-foreground">{testimonial.role}</div>
                  <div className="text-sm text-accent font-semibold">{testimonial.company}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Trust Badges */}
        <div className="mt-16 pt-16 border-t border-border">
          <div className="text-center mb-8">
            <h3 className="text-xl font-heading font-bold text-foreground mb-2">
              Trusted by Industry Leaders
            </h3>
            <p className="text-muted-foreground">
              Join hundreds of companies delivering exceptional customer support with AI
            </p>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-8">
            {['Fortune 500', 'SOC 2 Certified', 'GDPR Compliant', '99.9% Uptime', 'ISO 27001'].map((badge, index) =>
            <div key={index} className="flex items-center space-x-2 px-6 py-3 bg-muted/30 rounded-lg">
                <Icon name="ShieldCheckIcon" size={20} className="text-success" />
                <span className="text-sm font-heading font-semibold text-foreground">{badge}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

};

export default TestimonialsSection;